import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '../../store';
import apis from '../../utils/apis';
import logger from '../../utils/logger';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import GlassCard from '../shared/brand/GlassCard';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { PiTrashLight, PiPencilLight, PiLockKeyLight } from 'react-icons/pi';
import AdminRoleDetail from './AdminRoleDetail';
import { adminRolePath } from './adminRoutes';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';

/**
 * Roles & permissions — the Administration › Roles section.
 *
 * Same shape as the Users section: fixed filter chrome, and the table as the
 * one `min-h-0` scroll region inside the settings content pane.
 */

interface Role {
  id: string | number;
  name: string;
  description?: string;
  display_name?: string;
  permission_ids: number[];
  permission_count: number;
  is_active: boolean;
  is_custom: boolean;
  is_default: boolean;
  created_at: string;
  updated_at?: string;
  org_type?: string;
}

const AdminRolesList: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [roles, setRoles] = useState<Role[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const orgType =
      (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';

    const fetchRoles = async () => {
      try {
        setLoading(true);
        const response = await apis.get('/admin/roles', { params: { org_type: orgType } });

        if (response.data.success) {
          const rolesData = response.data.data || [];
          setRoles(rolesData);
          setFilteredRoles(rolesData);
        } else {
          dispatch(showFeedbackModal({
            showModal: true,
            message: toErrorText(response.data.error) || t('Failed to fetch roles'),
            modalType: 'error',
            duration: 4000
          }));
        }
      } catch (error: any) {
        logger.error('Error fetching roles', { status: error?.response?.status });
        dispatch(showFeedbackModal({
          showModal: true,
          message: readErrorMessage(error, t('Failed to fetch roles')),
          modalType: 'error',
          duration: 4000
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const reloadRoles = async () => {
    const orgType =
      (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';
    try {
      const response = await apis.get('/admin/roles', { params: { org_type: orgType } });
      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (error: any) {
      logger.error('Error reloading roles', { status: error?.response?.status });
    }
  };

  useEffect(() => {
    const filtered = roles.filter(
      (role) =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredRoles(filtered);
  }, [searchTerm, roles]);

  const handleSearchChange = (query: string) => {
    setSearchTerm(query);
  };

  const handleRoleClick = React.useCallback((roleId: string | number) => {
    history.push(adminRolePath(roleId));
  }, [history]);

  const roleFilters = [
    {
      id: 'Type',
      name: 'Type',
      options: [
        { value: 'custom', label: 'Custom' },
        { value: 'default', label: 'Default' },
      ],
    },
  ];

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    // Filter already applied through searchTerm
    // This is here for consistency with other pages
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
  };

  const handleEditRole = (role: Role) => {
    if (!role.is_custom) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Default roles cannot be edited'),
        modalType: 'warning',
        duration: 3000
      }));
      return;
    }
    const orgType =
      (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';
    dispatch(showModal({
      showModal: true,
      type: 'adminEditRole',
      role: role,
      message: t('Edit Role'),
      orgType: orgType,
      onConfirm: async () => {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Role updated successfully'),
          modalType: 'success',
          duration: 3000
        }));
        reloadRoles();
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  const deleteRole = async (roleId: string | number) => {
    try {
      const response = await apis.delete(`/admin/roles/${roleId}`);

      if (response.data.success) {
        setRoles(roles.filter((r) => r.id !== roleId));
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Role deleted successfully'),
          modalType: 'success',
          duration: 3000
        }));
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: toErrorText(response.data.error) || t('Failed to delete role'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      logger.error('Error deleting role', { status: error?.response?.status });
      dispatch(showFeedbackModal({
        showModal: true,
        message: readErrorMessage(error, t('Failed to delete role')),
        modalType: 'error',
        duration: 4000
      }));
    }
  };

  const handleDeleteRole = (role: Role) => {
    if (!role.is_custom) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Default roles cannot be deleted'),
        modalType: 'warning',
        duration: 3000
      }));
      return;
    }

    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: t('Delete role'),
      message: t('Delete the {{role}} role?', { role: formatRoleNameForDisplay(role.name) }),
      subText: t('Anyone holding it keeps their account but loses the permissions it granted.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete role'),
      onConfirm: () => {
        dispatch(hideModal());
        deleteRole(role.id);
      },
    }));
  };

  const columns = ['Name', 'Type', 'Permissions', 'Status', 'Created', 'Actions'];

  const tableData = filteredRoles.map((role) => ({
    id: role.id,
    'Name': (
      <span className="block truncate text-sm font-medium text-fg-strong">
        {formatRoleNameForDisplay(role.name)}
      </span>
    ),
    'Type': (
      <Badge color={role.is_custom ? 'blue' : 'gray'} className="text-[10px]">
        {role.is_custom ? t('Custom') : t('Default')}
      </Badge>
    ),
    'Permissions': (
      <span className="text-sm text-fg-body">
        {role.permission_count} {role.permission_count === 1 ? t('permission') : t('permissions')}
      </span>
    ),
    'Status': (
      <Badge color={role.is_active ? 'green' : 'gray'}>
        {role.is_active ? t('Active') : t('Inactive')}
      </Badge>
    ),
    'Created': (
      <span className="text-sm text-fg-muted">
        {new Date(role.created_at).toLocaleDateString()}
      </span>
    ),
    'Actions': null,
    rawRole: role,
  }));

  const roleActions = [
    {
      label: t('Edit'),
      onClick: (row: any) => handleEditRole(row.rawRole),
      icon: <PiPencilLight className="h-4 w-4" />,
      disabled: (row: any) => !row.rawRole.is_custom
    },
    {
      label: t('Delete'),
      onClick: (row: any) => handleDeleteRole(row.rawRole),
      icon: <PiTrashLight className="h-4 w-4 text-danger-600" />,
      isDestructive: true,
      disabled: (row: any) => !row.rawRole.is_custom
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <FilterBarWithTabs
        itemCount={filteredRoles.length}
        totalItemCount={roles.length}
        itemCountLabel="roles"
        filters={roleFilters}
        selectedFilters={{}}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onClearAllFilters={handleClearAllFilters}
        noParentPadding
      />

      <GlassCard padding="none" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Table
          tableId="admin-roles"
          columns={columns}
          data={tableData}
          actions={roleActions}
          onRowClick={(row) => handleRoleClick(row.id)}
          isLoading={loading}
          total_items={roles.length}
          total_showing_items={filteredRoles.length}
          setIsShowing={true}
          showingLabel={t('roles')}
          emptyState={{
            icon: <PiLockKeyLight />,
            headline: searchTerm ? t('No roles match that search') : t('No roles configured'),
            subtext: searchTerm
              ? t('Try a different name.')
              : t('Roles group the permissions you grant to people in your organization.'),
          }}
          defaultColumnWidths={{ Name: 220, Type: 110, Permissions: 150, Status: 110, Created: 130 }}
          min="min-h-0"
          max="max-h-full"
        />
      </GlassCard>
    </div>
  );
};

/**
 * The section as the settings shell mounts it — the list at
 * `/settings/admin-roles`, one role at `/settings/admin-roles/:detailId`.
 * See `AdminUsers` for why the section, not the shell, owns this decision.
 */
const AdminRoles: React.FC = () => {
  const { detailId } = useParams<{ detailId?: string }>();
  if (detailId) return <AdminRoleDetail embedded roleId={detailId} />;
  return <AdminRolesList />;
};

export default AdminRoles;
